import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  ArrowLeft, School, Users, CreditCard, GitBranch,
  CheckCircle, XCircle, Edit2, Save, X, Plus, Trash2,
  Calendar, Mail, Phone, MapPin, Building2,
} from "lucide-react";
import SuperAdminLayout from "@/components/ui/superadmin/SuperAdminLayout";
import { superAdminApi, type AdminSchool } from "@/hooks/api/superAdminApi";

const STATUS_OPTS = ["active", "inactive", "pending", "suspended"];
const TIER_OPTS   = ["basic", "standard", "premium", "enterprise"];
const STATUS_COLORS: Record<string, string> = {
  active:    "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  inactive:  "bg-slate-700/50 text-slate-400 border-slate-600",
  pending:   "bg-amber-500/10 text-amber-400 border-amber-500/20",
  suspended: "bg-red-500/10 text-red-400 border-red-500/20",
};

type Tab = "overview" | "subscription" | "branches";

export default function SchoolDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [school, setSchool]     = useState<AdminSchool | null>(null);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState<Tab>("overview");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");

  // Subscription edit state
  const [subEdit, setSubEdit]   = useState(false);
  const [subForm, setSubForm]   = useState({ plan: "", endDate: "", isActive: true, billingEmail: "" });

  // Branch state
  const [newBranch, setNewBranch] = useState({ name: "", city: "", phone: "", principalName: "" });
  const [addingBranch, setAddingBranch] = useState(false);

  // Status change
  const [statusSaving, setStatusSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await superAdminApi.getSchool(id);
        setSchool(res.school);
        const s = res.school;
        setSubForm({
          plan: s.subscription.plan,
          endDate: s.subscription.endDate ? s.subscription.endDate.slice(0, 10) : "",
          isActive: s.subscription.isActive,
          billingEmail: s.subscription.billingEmail,
        });
      } catch {
        setError("Failed to load school.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const flash = (msg: string, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(""), 4000); }
    else { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); }
  };

  const handleStatusChange = async (status: string) => {
    if (!school) return;
    setStatusSaving(true);
    try {
      const res = await superAdminApi.updateSchoolStatus(school._id, status);
      setSchool(res.school);
      flash("Status updated.");
    } catch { flash("Failed to update status.", true); }
    setStatusSaving(false);
  };

  const handleSubSave = async () => {
    if (!school) return;
    setSaving(true);
    try {
      const res = await superAdminApi.updateSubscription(school._id, {
        plan: subForm.plan,
        endDate: subForm.endDate || undefined,
        isActive: subForm.isActive,
        billingEmail: subForm.billingEmail,
      });
      setSchool(res.school);
      setSubEdit(false);
      flash("Subscription updated.");
    } catch { flash("Failed to update subscription.", true); }
    setSaving(false);
  };

  const handleAddBranch = async () => {
    if (!school || !newBranch.name.trim()) return;
    setAddingBranch(true);
    try {
      const branch = {
        id: `branch_${Date.now()}`,
        name: newBranch.name.trim(),
        address: { city: newBranch.city },
        phone: newBranch.phone || undefined,
        principalName: newBranch.principalName || undefined,
        isActive: true,
      };
      const updatedBranches = [...(school.branches ?? []), branch];
      const res = await superAdminApi.updateSchool(school._id, { branches: updatedBranches });
      setSchool(res.school);
      setNewBranch({ name: "", city: "", phone: "", principalName: "" });
      flash("Branch added.");
    } catch { flash("Failed to add branch.", true); }
    setAddingBranch(false);
  };

  const handleRemoveBranch = async (branchId: string) => {
    if (!school) return;
    try {
      const updatedBranches = school.branches.filter((b) => b.id !== branchId);
      const res = await superAdminApi.updateSchool(school._id, { branches: updatedBranches });
      setSchool(res.school);
      flash("Branch removed.");
    } catch { flash("Failed to remove branch.", true); }
  };

  const TABS: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: "overview",     label: "Overview",     icon: School },
    { key: "subscription", label: "Subscription", icon: CreditCard },
    { key: "branches",     label: "Branches",     icon: GitBranch },
  ];

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="h-8 bg-slate-800 rounded-xl w-48 animate-pulse" />
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 h-64 animate-pulse" />
        </div>
      </SuperAdminLayout>
    );
  }

  if (!school) {
    return (
      <SuperAdminLayout>
        <div className="text-center py-20 text-slate-500">School not found.</div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back + header */}
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate("/admin/schools")}
            className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors mt-0.5"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-white truncate">{school.name}</h1>
            <p className="text-slate-400 text-sm mt-0.5">{school.code} · {school.email}</p>
          </div>
          {/* Status pill + change */}
          <div className="flex items-center gap-2 shrink-0">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border capitalize ${STATUS_COLORS[school.status]}`}>
              {school.status}
            </span>
            <select
              value={school.status}
              disabled={statusSaving}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-300 focus:outline-none cursor-pointer disabled:opacity-50"
            >
              {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Flash messages */}
        {success && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3 text-emerald-400 text-sm">{success}</div>}
        {error   && <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm">{error}</div>}

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-900 border border-slate-800 rounded-2xl p-1">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                tab === key
                  ? "bg-slate-700 text-white"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* ── Overview ─────────────────────────────────────────────────── */}
        {tab === "overview" && (
          <div className="space-y-4">
            {/* Info cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">School Info</h3>
                <div className="space-y-2.5">
                  <InfoRow icon={Building2} label="Type" value={school.type ?? "—"} />
                  <InfoRow icon={MapPin} label="Address" value={`${school.address?.street}, ${school.address?.city}, ${school.address?.state}`} />
                  <InfoRow icon={Phone} label="Phone" value={school.phone} />
                  <InfoRow icon={Mail} label="Email" value={school.email} />
                </div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Principal</h3>
                <div className="space-y-2.5">
                  <InfoRow icon={Users} label="Name" value={school.principalName} />
                  <InfoRow icon={Mail} label="Email" value={school.principalEmail} />
                  <InfoRow icon={Phone} label="Phone" value={school.principalPhone} />
                </div>
                <div className="pt-2 border-t border-slate-800">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Admin Account</h3>
                  {typeof school.adminId === "object" && school.adminId ? (
                    <>
                      <InfoRow icon={Users} label="Name" value={school.adminId.name} />
                      <InfoRow icon={Mail} label="Email" value={school.adminId.email} />
                    </>
                  ) : <p className="text-sm text-slate-500">No admin linked</p>}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Statistics</h3>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: "Students", value: school.statistics?.totalStudents ?? 0 },
                  { label: "Teachers", value: school.statistics?.totalTeachers ?? 0 },
                  { label: "Staff",    value: school.statistics?.totalStaff ?? 0 },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-slate-800 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-white">{value}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Subscription ─────────────────────────────────────────────── */}
        {tab === "subscription" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-white">Subscription</h3>
              {!subEdit ? (
                <button
                  onClick={() => setSubEdit(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-xs font-semibold text-slate-300 hover:text-white transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setSubEdit(false)} className="flex items-center gap-1 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-xl text-xs text-slate-400 transition-colors">
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                  <button
                    onClick={handleSubSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-500 rounded-xl text-xs font-semibold text-white transition-colors disabled:opacity-50"
                  >
                    <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              )}
            </div>

            {!subEdit ? (
              <div className="grid sm:grid-cols-2 gap-4">
                <InfoRow icon={CreditCard} label="Plan" value={<span className="capitalize font-semibold">{school.subscription.plan}</span>} />
                <InfoRow icon={CheckCircle} label="Active" value={school.subscription.isActive ? "Yes" : "No"} />
                <InfoRow icon={Calendar} label="Expires" value={school.subscription.endDate ? new Date(school.subscription.endDate).toLocaleDateString() : "—"} />
                <InfoRow icon={Mail} label="Billing email" value={school.subscription.billingEmail} />
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField label="Plan">
                  <select value={subForm.plan} onChange={(e) => setSubForm((f) => ({ ...f, plan: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500">
                    {TIER_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </FormField>
                <FormField label="Expiry date">
                  <input type="date" value={subForm.endDate} onChange={(e) => setSubForm((f) => ({ ...f, endDate: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500" />
                </FormField>
                <FormField label="Billing email">
                  <input type="email" value={subForm.billingEmail} onChange={(e) => setSubForm((f) => ({ ...f, billingEmail: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500" />
                </FormField>
                <FormField label="Active">
                  <label className="flex items-center gap-3 cursor-pointer mt-1">
                    <div
                      onClick={() => setSubForm((f) => ({ ...f, isActive: !f.isActive }))}
                      className={`relative w-10 h-5 rounded-full transition-colors ${subForm.isActive ? "bg-teal-500" : "bg-slate-700"}`}
                    >
                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${subForm.isActive ? "translate-x-5" : ""}`} />
                    </div>
                    <span className="text-sm text-slate-300">{subForm.isActive ? "Active" : "Inactive"}</span>
                  </label>
                </FormField>
              </div>
            )}
          </div>
        )}

        {/* ── Branches ─────────────────────────────────────────────────── */}
        {tab === "branches" && (
          <div className="space-y-4">
            {/* Existing branches */}
            {(school.branches ?? []).length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-10 text-center text-slate-500">
                <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No branches added yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {school.branches.map((b) => (
                  <div key={b.id} className="flex items-center gap-4 bg-slate-900 border border-slate-800 rounded-2xl px-5 py-4">
                    <div className="w-9 h-9 rounded-xl bg-slate-800 flex items-center justify-center shrink-0">
                      <GitBranch className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{b.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {b.principalName ? `Principal: ${b.principalName}` : ""}
                        {b.phone ? ` · ${b.phone}` : ""}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${b.isActive ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>
                      {b.isActive ? "Active" : "Inactive"}
                    </span>
                    <button
                      onClick={() => handleRemoveBranch(b.id)}
                      className="p-1.5 rounded-lg hover:bg-red-500/10 text-slate-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add branch form */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Plus className="w-4 h-4 text-teal-400" /> Add Branch
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <FormField label="Branch name *">
                  <input type="text" value={newBranch.name} onChange={(e) => setNewBranch((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Victoria Island Campus"
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500" />
                </FormField>
                <FormField label="City">
                  <input type="text" value={newBranch.city} onChange={(e) => setNewBranch((f) => ({ ...f, city: e.target.value }))}
                    placeholder="e.g. Lagos"
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500" />
                </FormField>
                <FormField label="Principal name">
                  <input type="text" value={newBranch.principalName} onChange={(e) => setNewBranch((f) => ({ ...f, principalName: e.target.value }))}
                    placeholder="e.g. Dr. Jane Doe"
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500" />
                </FormField>
                <FormField label="Phone">
                  <input type="text" value={newBranch.phone} onChange={(e) => setNewBranch((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+234 800 000 0000"
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500" />
                </FormField>
              </div>
              <button
                onClick={handleAddBranch}
                disabled={addingBranch || !newBranch.name.trim()}
                className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
              >
                <Plus className="w-4 h-4" /> {addingBranch ? "Adding…" : "Add Branch"}
              </button>
            </div>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <span className="text-xs text-slate-500">{label}</span>
        <p className="text-sm text-slate-200 mt-0.5 break-words">{value ?? "—"}</p>
      </div>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-slate-400">{label}</label>
      {children}
    </div>
  );
}
